import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const PlatformAuthCtx = createContext({ admin: null, login: async () => {}, logout: async () => {}, refresh: async () => {} });

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

/**
 * `platformApi` — Platform-Console-only axios instance.
 *
 * IMPORTANT — this is a DIFFERENT client from the tenant `api` in
 * `frontend/src/lib/api.js`. Never share instances: the tenant instance
 * carries its own base URL + refresh flow using tenant cookies
 * (`refresh_token`), while this one only ever sees platform cookies
 * (`platform_access_token` / `platform_refresh_token`) issued by
 * `/api/platform/auth/*`.
 */
export const platformApi = axios.create({ baseURL: API, withCredentials: true });

/**
 * `refreshClient` — bare axios instance dedicated to hitting
 * `POST /platform/auth/refresh` when we get a 401 from the primary
 * `platformApi`. Kept SEPARATE so the response interceptor cannot
 * recursively call itself on a refresh failure.
 */
const refreshClient = axios.create({ baseURL: API, withCredentials: true });

/**
 * Phase PLATFORM-REFRESH (2026-07-09) — Platform 401 interceptor.
 * Phase PLATFORM-REFRESH-HOTFIX (2026-07-09) — route-scoped redirect.
 *
 * The FIRST version of this interceptor caused a preview refresh loop:
 * `PlatformAuthProvider` mounts app-wide (tenant + platform routes),
 * so the boot-time `/platform/auth/me` probe hit 401 on every tenant
 * page → interceptor tried `/auth/refresh` → 401 → hard-redirect to
 * `/platform/login`. On the platform login page, the same probe fired
 * and re-triggered the redirect → infinite ping-pong between
 * `/login` ↔ `/platform/login`.
 *
 * Fix: TWO extra guards.
 *   (a) URL guard — never touch `/auth/me` probes. A 401 there is the
 *       legitimate "not logged in" signal the context already handles
 *       via `setAdmin(false)`. Same treatment for `/auth/login` and
 *       `/auth/refresh` (avoids recursion).
 *   (b) Route guard — only hard-redirect when the current pathname is
 *       inside `/platform/*` AND not already `/platform/login`. For
 *       any other route (tenant `/`, `/login`, `/dashboard`, `/cases`,
 *       …) let the 401 bubble to the caller silently. The screen the
 *       user is actually looking at was never a platform screen; a
 *       forced redirect there is what caused the loop.
 *
 * Refresh-on-401 for genuine platform requests still works — pricing
 * PUT / matrix GET / countries etc. all fire from inside `/platform/*`
 * where the route guard fires normally.
 */
let notifyAdminExpired = null;

function isPlatformAuthProbeUrl(url) {
  if (!url) return false;
  // Match both bare paths and full URLs.
  return url.includes("/auth/me")
    || url.includes("/auth/refresh")
    || url.includes("/auth/login");
}

function shouldRedirectOnPlatformAuthFailure() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  // Only redirect when the visible page is a platform page other than
  // the login itself. Tenant pages must never be nudged toward the
  // platform login — that was the loop trigger.
  return path.startsWith("/platform") && !path.startsWith("/platform/login");
}

function goToPlatformLogin() {
  try {
    if (typeof window === "undefined") return;
    if (!shouldRedirectOnPlatformAuthFailure()) return;
    const here = window.location.pathname + window.location.search;
    const next = `?next=${encodeURIComponent(here)}`;
    window.location.assign(`/platform/login${next}`);
  } catch (e) { /* ignore */ }
}

platformApi.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    if (status !== 401 || !original) return Promise.reject(err);
    // (a) URL guard — probe endpoints never trigger refresh/redirect.
    if (isPlatformAuthProbeUrl(original.url)) return Promise.reject(err);
    // (b) Only mid-flight platform requests (from real platform pages)
    // should attempt refresh + retry + redirect. Anything else — the
    // boot-time probe from a tenant page, background polling on the
    // login screen — MUST return the raw 401 silently.
    if (!shouldRedirectOnPlatformAuthFailure()) return Promise.reject(err);
    if (original._platformRetry) {
      if (typeof notifyAdminExpired === "function") notifyAdminExpired();
      goToPlatformLogin();
      return Promise.reject(err);
    }
    original._platformRetry = true;
    try {
      await refreshClient.post("/auth/refresh");
    } catch (refreshErr) {
      if (typeof notifyAdminExpired === "function") notifyAdminExpired();
      goToPlatformLogin();
      return Promise.reject(refreshErr);
    }
    return platformApi.request(original);
  },
);

export function PlatformAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wire the interceptor's session-expired callback to context state
  // so screens using `usePlatformAuth()` observe the logout instantly.
  useEffect(() => {
    notifyAdminExpired = () => setAdmin(false);
    return () => { notifyAdminExpired = null; };
  }, []);

  const fetchMe = async () => {
    try {
      const r = await platformApi.get("/auth/me");
      setAdmin(r.data.data);
    } catch {
      setAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMe(); }, []);

  const login = async (email, password, totpCode) => {
    const r = await platformApi.post("/auth/login", { email, password, totp_code: totpCode || null });
    setAdmin(r.data.data);
    return r.data.data;
  };

  const logout = async () => {
    try { await platformApi.post("/auth/logout"); } catch { /* ignore */ }
    setAdmin(false);
  };

  return (
    <PlatformAuthCtx.Provider value={{ admin, loading, login, logout, refresh: fetchMe }}>
      {children}
    </PlatformAuthCtx.Provider>
  );
}

export const usePlatformAuth = () => useContext(PlatformAuthCtx);
